import { chain, map } from 'fp-ts/lib/TaskEither';
import { flow, pipe } from 'fp-ts/lib/function';
import { Type } from '@/entities/todo';
import { listCodec, paginationCountCodec } from './codec';
import {
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createAuthorizedPutRequest,
  decode,
  decodeId,
  fetchApi,
  numericEnum,
  setBody,
  setQuery,
} from '../..';
import { encodeInspectionQuery, InspectionQuery } from './query';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';
import { unPaginatedRecord } from '@/services/api/resources/issue/v2';

const baseUrl = '/v2/todos';

const legacyBaseUrl = '/v1/inspections';
const legacyCreateUrl = (timezone: string): string => `${legacyBaseUrl}?timezone=${timezone}`;
const legacyUpdateUrl = (id: number, timezone: string): string => `${legacyBaseUrl}/${id}?timezone=${timezone}`;
const legacyRecordUrl = (id: number): string => `${legacyBaseUrl}/${id}`;
const getPaginationCountUrl = (): string => `${baseUrl}/pagination`;

export const listInspectionIssues = (id: string | number) => pipe(
  `/v2/inspections/${id}/issues`,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(unPaginatedRecord)),
);

export const list = (query: InspectionQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery({
    ...encodeInspectionQuery(query),
    type: numericEnum(Type.Inspection),
  })),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const getPagination = (query: InspectionQuery) => pipe(
  getPaginationCountUrl(),
  createAuthorizedGetRequest,
  map(setQuery({
    ...encodeInspectionQuery(query),
    type: numericEnum(Type.Inspection),
  })),
  chain(fetchApi),
  chain(decode(paginationCountCodec)),
);

export const create = (inspection: unknown, timezone: string) => pipe(
  timezone,
  legacyCreateUrl,
  createAuthorizedPostRequest,
  map(setBody(inspection)),
  chain(fetchApi),
);

export const get = flow(
  legacyRecordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
);

export const update = (id: number, inspection: unknown, timezone: string) => pipe(
  legacyUpdateUrl(id, timezone),
  createAuthorizedPutRequest,
  map(setBody(inspection)),
  chain(fetchApi),
  chain(decodeId),
);

export const remove = (id: number) => pipe(
  id,
  legacyRecordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, '/v2/inspections');
